var config        = require("app/config"),
    get           = require("object-path").get,
    logger        = require("enyo/logger"),
    quickMacroize = require("enyo/macroize").quickMacroize,
    resoleAudioAdSchedule,
    resolveVideoAdPixel,
    resolveVideoAdTagUrl,
    rnd           = Date.now();

/**
 * Resolves the ad setup for Radiant Media Player
 *
 * @param    {Object}          session         The watching session
 * @param    {Object}          media           The media item
 * @param    {Object}          asset           The media asset
 * @param    {Object}          i18nService     The i18n service
 * @param    {PrivacyService}  privacyService  The privacy service
 * @returns  {Object}          The ad setup
 */
module.exports = function(
    session,
    media,
    asset,
    i18nService,
    privacyService
) {
    var setup     = {},
        mediaType = get(media, "type", null),
        adType    = get(session, "ad_type", "none");

    // No ads at all
    if (!get(config, "adAlliance.enabled", false)) {
        setup.ads = false;

        return setup;
    }

    // Audio ads
    //
    // !IMPORTANT: Ad switching does *not* work for adSchedules in RadiantMP v9.
    // !IMPORTANT: You can only enable ads on player initialization and the
    // !IMPORTANT: schedule will then be applied to all items played during that
    // !IMPORTANT: player instance's lifetime!
    if (["audio", "album_track", "podcast_episode"].includes(mediaType)) {

        // No audio ads
        if (!get(config, "adAlliance.audio.enabled", false)) {
            setup.ads = false;

            return setup;
        }

        // Setup Audio ads
        setup.ads        = true;
        setup.adSchedule = resoleAudioAdSchedule(adType, asset);
        setup.adLocale   = i18nService.get("language");
    }

    // Video ads
    if (["episode", "video"].includes(mediaType)) {

        // No video ads at all
        if (!get(config, "adAlliance.video.enabled", false)) {
            setup.ads = false;

            return setup;
        }

        // Setup Video ads
        setup.ads                = true;
        setup.adTagUrl           = resolveVideoAdTagUrl(adType, media, asset, privacyService);
        setup.smartclipPixel     = resolveVideoAdPixel(adType, privacyService);
        setup.adLocale           = i18nService.get("language");
        setup.adEnablePreloading = true;
        setup.adCountDown        = true;
    }

    return setup;
};

/**
 * Determines the ad schedule for the provided media and asset.
 *
 * @param    {string}  adType  The ad type
 * @param    {Object}  asset   The media asset
 * @returns  {Object}  The ad schedule
 */
resoleAudioAdSchedule = function(adType, asset) {
    var schedule = {},
        duration = get(asset, "duration", 0),
        preRollUrl,
        midRollUrl,
        midRollFrequency,
        midRollCount,
        segmentLength,
        i,
        timestamp,
        postRollUrl;

    // Pre-roll
    preRollUrl = get(config, quickMacroize("adAlliance.audio.adType.{$adType}.preRollUrl", {
        adType: adType
    }), null);

    if (preRollUrl) {
        schedule.preroll = preRollUrl;
    }

    // Mid-roll
    midRollUrl       = get(config, quickMacroize("adAlliance.audio.adType.{$adType}.midRollUrl", {
        adType: adType
    }), null);
    midRollFrequency = get(config, quickMacroize("adAlliance.audio.adType.{$adType}.midRollFrequency", {
        adType: adType
    }), null);

    if (midRollUrl && midRollFrequency) {
        schedule.midroll = [];

        midRollCount = Math.floor(duration / midRollFrequency);

        if (midRollCount > 0) {
        // Calculate the length of each segment between midrolls
            segmentLength = Math.floor(duration / (midRollCount + 1));

            // Place midrolls evenly spaced
            for (i = 1; i <= midRollCount; i++) {
                timestamp = i * segmentLength;
                schedule.midroll.push([timestamp, midRollUrl]);
            }
        }
    }

    // Post-roll
    postRollUrl = get(config, quickMacroize("adAlliance.audio.adType.{$adType}.postRollUrl", {
        adType: adType
    }), null);

    if (postRollUrl) {
        schedule.postroll = postRollUrl;
    }

    return schedule;
};

/**
 * Determines the adTagUrl for the provided media and asset.
 *
 * @param    {string}          adType          The ad type
 * @param    {Object}          media           The media item
 * @param    {Object}          asset           The media asset
 * @param    {PrivacyService}  privacyService  The privacy service
 * @returns  {string}          The adTagUrl
 */
resolveVideoAdTagUrl = function(adType, media, asset, privacyService) {
    var url,
        params,
        iabConsentData = privacyService.get("iabConsentData");

    // Ad tag url
    url = get(config, quickMacroize("adAlliance.video.adType.{$adType}.url", {
        adType: adType
    }), null);

    // RadiantMP does not have a way to switch from showing ads to showing no
    // ads at all. The current solution is to use an adTagUrl without any vmap
    // placements instead.
    //
    // While this *really* should be handled from the config (as there is no
    // guarantee smartclip will remain the only ad provider), we hard-code this
    // url here as a stopgap to prevent showing unwanted ads on production
    // environments.
    if (!url) {
        logger.warn("Ad tag url missing in config. It is highly recommended you either add one or disable video ads. Read the comments above this log statement for more info.");

        return "https://ad.sxp.smartclip.net/select?optout=1&type=vast4:vmapuri";
    }

    // No-ads variant
    if (adType === "none") {
        return url;
    }

    url = new URL(url);

    // Mandatory parameters
    params = url.searchParams;
    params.set("rnd", rnd);
    params.set("ang_ref", window.location.href);

    if (privacyService.shouldBypassConsent()) {
        params.set("optout", 0);
    }
    else if (iabConsentData && iabConsentData.tcString) {
        params.set("consent", iabConsentData.tcString);
    }
    else {
        params.set("optout", 1);
    }

    // Optional parameters
    if (media) {
        params.set("vodid", media.id || null);
        params.set("fwd_contenttype", media.type || null);

        if (["audio", "video"].includes(media.type)) {
            params.set("fwd_program", get(media, "meta.title", null));
        }
        else if (["album_track", "podcast_episode"].includes(media.type)) {
            params.set("fwd_program", get(media, ".parent.meta.title", null));
            params.set("fwd_episode", get(media, "meta.title", null));
        }
        else if (media.type === "episode") {
            params.set("fwd_program", get(media, "parent.parent.meta.title", null));
            params.set("fwd_seizoen", get(media, "parent.meta.title", null));
            params.set("fwd_episode", get(media, "meta.title", null));
        }
    }
    params.set("fwd_playername", "Radiant Media Player");
    params.set("fwd_playerversion", get(config, "radiantMediaPlayer.version", null));

    if (asset) {
        params.set("ang_conlen", asset.duration || null);
    }
    params.set("fwd_theme", get(config, "theme", null));
    params.set("fwd_artist", get(media, "artists", []).join(", "));
    params.set("fwd_genre", get(media, "genres", []).join(", "));

    return url.toString();
};

/**
 * Determines the pixel for video ads.
 *
 * @param    {string}          adType          The ad type
 * @param    {PrivacyService}  privacyService  The privacy service
 * @returns  {string|null}     The adTagUrl
 */
resolveVideoAdPixel = function(adType, privacyService) {
    var url,
        params,
        iabConsentData  = privacyService.get("iabConsentData");

    // Smartclip Pixel url
    url = get(config, quickMacroize("adAlliance.video.adType.{$adType}.pixel", {
        adType: adType
    }), null);

    // No pixel for this adType
    if (!url) {
        return null;
    }

    // Setup pixel
    url = new URL(url);

    params = url.searchParams;

    if (privacyService.shouldBypassConsent()) {
        params.set("optout", 0);
    }
    else if (iabConsentData && iabConsentData.tcString) {
        params.set("consent", iabConsentData.tcString);
    }
    else {
        params.set("optout", 1);
    }
    params.set("rnd", rnd);

    return url.toString();
};
